/**
 * BWF (Broadcast Wave Format) header writer for the RT session recorder.
 *
 * Produces a RIFF/WAVE header with:
 *   - `fmt ` chunk: PCM, 48 kHz, 24-bit, mono (fixed per RT spec)
 *   - `bext` chunk: EBU Tech 3285 v2 — 602 fixed bytes + CodingHistory tail
 *   - `data` chunk header (size patched as the recording grows)
 *
 * Byte layout with the default CodingHistory (42 bytes):
 *
 *   offset  size  field
 *   ------  ----  -----------------------------------------------
 *        0     4  'RIFF'
 *        4     4  RIFF size (u32 LE)            ← patched live
 *        8     4  'WAVE'
 *       12     4  'fmt '
 *       16     4  16 (u32 LE)
 *       20     2  formatCode = 1 (PCM)
 *       22     2  channels = 1
 *       24     4  sampleRate = 48000
 *       28     4  byteRate = 144000
 *       32     2  blockAlign = 3
 *       34     2  bitsPerSample = 24
 *       36     4  'bext'
 *       40     4  bext size = 602 + len(CodingHistory) (u32 LE)
 *       44   256  Description (ASCII, NUL-padded)
 *      300    32  Originator
 *      332    32  OriginatorReference
 *      364    10  OriginationDate  YYYY-MM-DD
 *      374     8  OriginationTime  HH:MM:SS
 *      382     4  TimeReferenceLow  (u32 LE)
 *      386     4  TimeReferenceHigh (u32 LE)
 *      390     2  Version = 2 (u16 LE)
 *      392    64  UMID (zeroed)
 *      456    10  Loudness ×5 s16 (zeroed)
 *      466   180  Reserved (zeroed)
 *      646   var  CodingHistory (default 42 B, CR/LF-terminated, even-padded)
 *      688     4  'data'
 *      692     4  data size (u32 LE)            ← patched live
 *      696        ← PCM payload starts here (with default CodingHistory)
 */

/** RT session fixed format. */
export const BWF_SAMPLE_RATE = 48000
export const BWF_BIT_DEPTH = 24
export const BWF_CHANNELS = 1
export const BWF_BYTES_PER_SAMPLE = 3

/** EBU 3285 v2 fixed part of the bext chunk (everything before CodingHistory). */
export const BEXT_FIXED_SIZE = 602

export const DEFAULT_CODING_HISTORY = 'A=PCM,F=48000,W=24,M=mono,T=OHEAR-RT v1'

export interface BwfHeaderParams {
  /** TimeReference: samples since midnight @ 48 kHz. Written Low/High u32 LE. */
  tcStartSamples: number
  /** bext Originator (≤32 ASCII chars; truncated + NUL-padded). */
  originator: string
  /** bext OriginatorReference (≤32 ASCII chars). */
  originatorRef: string
  /** bext Description (≤256 ASCII chars). */
  description: string
  /** Creation timestamp for OriginationDate/Time. Defaults to `new Date()`. */
  date?: Date
  /** Override CodingHistory line. CR/LF appended if missing; padded to even. */
  codingHistory?: string
}

/**
 * Build the full BWF header (RIFF + fmt + bext + data chunk header).
 * RIFF size and data size are written as 0 — call {@link patchSizes} to get
 * the write-back values/offsets as the data chunk grows.
 */
export function writeBwfHeader(params: BwfHeaderParams): Uint8Array {
  let ch = params.codingHistory ?? DEFAULT_CODING_HISTORY
  if (!ch.endsWith('\r\n')) ch += '\r\n'
  // RIFF chunks are word-aligned; keep CodingHistory even so the data chunk
  // header starts on an even boundary without a pad byte.
  if (ch.length % 2 === 1) ch += '\0'

  const bextSize = BEXT_FIXED_SIZE + ch.length
  const total = 12 + 24 + (8 + bextSize) + 8
  const out = new Uint8Array(total)
  const view = new DataView(out.buffer)

  // ---- RIFF ----
  writeAscii(out, 0, 'RIFF')
  view.setUint32(4, 0, true) // patched later
  writeAscii(out, 8, 'WAVE')

  // ---- fmt ----
  writeAscii(out, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, BWF_CHANNELS, true)
  view.setUint32(24, BWF_SAMPLE_RATE, true)
  view.setUint32(28, BWF_SAMPLE_RATE * BWF_BYTES_PER_SAMPLE * BWF_CHANNELS, true)
  view.setUint16(32, BWF_BYTES_PER_SAMPLE * BWF_CHANNELS, true)
  view.setUint16(34, BWF_BIT_DEPTH, true)

  // ---- bext ----
  writeAscii(out, 36, 'bext')
  view.setUint32(40, bextSize, true)
  const b = 44
  writeAsciiField(out, b, params.description, 256)
  writeAsciiField(out, b + 256, params.originator, 32)
  writeAsciiField(out, b + 288, params.originatorRef, 32)

  const d = params.date ?? new Date()
  writeAsciiField(out, b + 320, formatBextDate(d), 10)
  writeAsciiField(out, b + 330, formatBextTime(d), 8)

  // TimeReference — 64-bit sample count split into two u32 LE words.
  const tc = Math.max(0, Math.floor(params.tcStartSamples))
  const low = tc % 0x1_0000_0000
  const high = Math.floor(tc / 0x1_0000_0000)
  view.setUint32(b + 338, low, true)
  view.setUint32(b + 342, high, true)

  view.setUint16(b + 346, 2, true) // bext Version = 2
  // UMID (64 B) + loudness (10 B) + reserved (180 B) stay zeroed.
  writeAscii(out, b + BEXT_FIXED_SIZE, ch)

  // ---- data chunk header ----
  const dataHdr = 44 + bextSize
  writeAscii(out, dataHdr, 'data')
  view.setUint32(dataHdr + 4, 0, true) // patched later

  return out
}

/** A single positional write-back: `bytes` go at absolute file `offset`. */
export interface SizePatch {
  offset: number
  bytes: Uint8Array
}

/**
 * Compute the RIFF-size and data-size write-backs for a file whose header is
 * `headerBytes` long (as returned by {@link writeBwfHeader}) and whose data
 * chunk currently holds `dataBytes` of PCM.
 *
 * Per RIFF spec an odd-sized data chunk is followed by one pad byte which is
 * counted in the RIFF size but NOT in the data size; the returned RIFF value
 * accounts for it. (The 24-bit worklet path always appends multiples of 384
 * bytes, so the pad only matters for the final flush.)
 */
export function patchSizes(
  headerBytes: number,
  dataBytes: number,
): { riff: SizePatch; data: SizePatch } {
  const pad = dataBytes % 2
  return {
    riff: { offset: 4, bytes: u32le(headerBytes - 8 + dataBytes + pad) },
    data: { offset: headerBytes - 4, bytes: u32le(dataBytes) },
  }
}

/**
 * Format a sample position (samples since midnight @48 kHz) as the 8-digit
 * `HHMMSSFF` timecode used in RT file names, e.g. tc 01:00:30:00 @24fps →
 * "01003000".
 */
export function formatTimecode(samples: number, fps = 24): string {
  const totalSec = Math.floor(samples / BWF_SAMPLE_RATE)
  const hh = Math.floor(totalSec / 3600) % 24
  const mm = Math.floor((totalSec % 3600) / 60)
  const ss = totalSec % 60
  const ff = Math.floor(((samples % BWF_SAMPLE_RATE) * fps) / BWF_SAMPLE_RATE)
  return [hh, mm, ss, ff].map((n) => String(n).padStart(2, '0')).join('')
}

// ---------- helpers ----------

function u32le(value: number): Uint8Array {
  const b = new Uint8Array(4)
  new DataView(b.buffer).setUint32(0, value >>> 0 === value ? value : value % 0x1_0000_0000, true)
  return b
}

function writeAscii(out: Uint8Array, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) out[offset + i] = text.charCodeAt(i) & 0x7f
}

/**
 * Truncate to `size` chars, write ASCII, leave the rest NUL (buffer is
 * zeroed). bext fields are ASCII-only per EBU 3285 — anything outside the
 * printable range is replaced with '?' (the sidecar keeps the full string).
 */
function writeAsciiField(out: Uint8Array, offset: number, text: string, size: number): void {
  const t = text.length > size ? text.slice(0, size) : text
  for (let i = 0; i < t.length; i++) {
    const c = t.charCodeAt(i)
    out[offset + i] = c >= 0x20 && c <= 0x7e ? c : 0x3f // '?'
  }
}

function formatBextDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${String(y)}-${m}-${day}`
}

function formatBextTime(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${h}:${m}:${s}`
}
